import type { DropdownMenuItem } from "../components/dropdown-menu/DropdownMenu";
import {
  isClientGroupPosition,
  isProducerGroupPosition,
  isSpecialistPosition,
} from "./memberPositionUtils";

/** メニュー対象メンバーの最小形（バナー/管理モーダルの membership 互換） */
export interface MembershipMenuTarget {
  position: string;
  is_invited?: boolean;
}

export interface BuildMembershipMenuOptions<T extends MembershipMenuTarget> {
  /**
   * クライアントグループの行を操作できるか。
   * バナー: クライアント責任者またはプロデューサー責任者 / 管理画面: 管理者。
   */
  canControlClients: boolean;
  /**
   * プロデューサーグループとスペシャリストの行を操作できるか。
   * バナー: プロデューサー責任者のみ / 管理画面: 管理者。
   */
  canControlProducers: boolean;
  /** 招待中を除く CLIENT（クライアント責任者）の人数 */
  primaryClientCount: number;
  /** 招待中を除く PRODUCER（プロデューサー責任者）の人数 */
  primaryProducerCount: number;
  onUpdatePosition: (_member: T, _newPosition: string) => void;
  onOpenIpRestriction: (_member: T) => void;
  onRemove: (_member: T) => void;
}

/**
 * プロジェクトメンバー 1 行分の 3 点リーダーメニューを生成する純関数。
 *
 * プロジェクトバナーのユーザーリスト（責任者視点）と管理画面の
 * メンバーシップモーダル（管理者視点）で、同じ項目・同じガード
 * （最後の責任者は解除 / 退出不可など）を共有する。
 */
export const buildMembershipMenuItems = <T extends MembershipMenuTarget>(
  member: T,
  {
    canControlClients,
    canControlProducers,
    primaryClientCount,
    primaryProducerCount,
    onUpdatePosition,
    onOpenIpRestriction,
    onRemove,
  }: BuildMembershipMenuOptions<T>,
): DropdownMenuItem[] => {
  const { position } = member;

  // 招待中ユーザーはドロップダウンメニューを使わない（再送信 / 取り消しボタンで操作）
  if (member.is_invited) return [];

  const items: DropdownMenuItem[] = [];

  if (canControlClients && isClientGroupPosition(position)) {
    if (position === "CLIENT" && primaryClientCount > 1) {
      items.push({
        label: "責任者権限を解除",
        onClick: () => onUpdatePosition(member, "REVIEWER"),
        icon: "person_remove",
      });
    } else if (position === "REVIEWER") {
      items.push({
        label: "責任者権限を付与",
        onClick: () => onUpdatePosition(member, "CLIENT"),
        icon: "person_add",
      });
    }
    items.push({
      label: "IP制限",
      onClick: () => onOpenIpRestriction(member),
      icon: "vpn_lock",
    });
    // 最後のクライアント責任者は退出させられない
    if (position !== "CLIENT" || primaryClientCount > 1) {
      items.push({
        label: "プロジェクトから退出",
        onClick: () => onRemove(member),
        icon: "logout",
      });
    }
  }

  if (canControlProducers && isProducerGroupPosition(position)) {
    if (position === "PRODUCER" && primaryProducerCount > 1) {
      items.push({
        label: "責任者権限を解除",
        onClick: () => onUpdatePosition(member, "DIRECTOR"),
        icon: "person_remove",
      });
    } else if (position === "DIRECTOR") {
      items.push({
        label: "責任者権限を付与",
        onClick: () => onUpdatePosition(member, "PRODUCER"),
        icon: "person_add",
      });
    }
    items.push({
      label: "IP制限",
      onClick: () => onOpenIpRestriction(member),
      icon: "vpn_lock",
    });
    // 最後のプロデューサー責任者はスペシャリストに降格できない
    if (position !== "PRODUCER" || primaryProducerCount > 1) {
      items.push({
        label: "スペシャリストとして専念",
        onClick: () => onUpdatePosition(member, "SPECIALIST"),
        icon: "engineering",
      });
    }
  }

  // スペシャリストをプロデューサーグループへ招聘
  if (canControlProducers && isSpecialistPosition(position)) {
    items.push({
      label: "プロデューサー（責任者）に招聘",
      onClick: () => onUpdatePosition(member, "PRODUCER"),
      icon: "person_add",
    });
    items.push({
      label: "プロデューサーに招聘",
      onClick: () => onUpdatePosition(member, "DIRECTOR"),
      icon: "person_add",
    });
  }

  return items;
};
